import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { ApiService } from '../api.service';
import { UtilsService } from '../shared/utils/utils.service';
import { RoleType } from '../../shared/enums/role-type/role-type.module';
import { Router } from '@angular/router';
import { Jwt } from '../../models/jwt.model';
import { Token } from '../../models/token.model';
import { GeneralParameter } from '../../models/generalParameter.model';
import { environment } from '../environment/environment';
import { InteractionStatus } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private router: Router,
    private http: HttpClient,
    private msalService: MsalService,
    private apiService: ApiService,
    private utils: UtilsService,
    private msalBroadcastService: MsalBroadcastService)
  {
    this.initialize();
  }

  apiUrl = `${this.apiService.apiUrl}/auth`;

  rolesClaim = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
  userIdClaim = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";
  nameClaim = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";
  emailClaim = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";
  expirationClaim = "http://schemas.microsoft.com/ws/2008/06/identity/claims/expiration";
  ssoClaim = "sso";
  customerIdClaim = "customerid";

  private async initialize(): Promise<void> {
    try {
      const result = await this.msalService.instance.handleRedirectPromise();
      if (!result && this.msalService.instance.getAllAccounts().length === 0) {
        this.login();
      }
      else{
        this.loadUserRole();
      }
    } catch (error) {

    }
  }

  login(): void {
    this.removeToken();
    this.removeCustomer();

    if (this.msalService.instance.getAllAccounts().length > 0) {
      this.msalService.instance.clearCache();
    }

    if (this.msalService.instance.getAllAccounts().length <= 0) {
      this.msalService.loginRedirect();
    }
  }

  logout(reason: string = 'logout'): void {
    this.setAuthState(reason);
    this.router.navigate(['logout']);
  }



  transformToken(isToReset: boolean = false): Observable<boolean> {
    if (!this.getToken() || isToReset) {
      const account = this.msalService.instance.getAllAccounts()[0];
      if (account) {
        this.msalService.instance.setActiveAccount(account);
        return new Observable<boolean>((observer) => {
          this.msalService.instance.acquireTokenSilent({ scopes: environment.azureAd.scopes })
            .then(result => {
              this.http.post<{ token: Token }>(`${this.apiUrl}/transform-token`, { IdToken: result.idToken })
                .pipe(
                  map(response => {
                    this.setAuthState('logged');
                    this.setToken(response.token.access_token);
                    observer.next(true);
                    observer.complete();
                  })
                ).subscribe({
                  error: (err) => {
                    observer.next(false);
                    observer.complete();
                  },
                });
            })
            .catch(error => {
              observer.next(false);
              observer.complete();
            });
        });
      } else {
        // Return an observable that emits false and completes immediately
        return of(false);
      }
    } else {
      // Return an observable that emits true and completes immediately
      return of(false);
    }
  }

  createSSOToken(sso: string): Observable<boolean> {
    if (!this.getToken()) {
      return new Observable<boolean>((observer) => {
        this.http.post<{ token: Token }>(`${this.apiUrl}/sso`, { IdToken: sso })
          .pipe(
            map(response => {
              this.setAuthState('logged');
              this.setToken(response.token.access_token);
              this.setCustomer(response.token.customerId);
              observer.next(true);
              observer.complete();
            })
          ).subscribe({
            error: (err) => {
              console.log(err);
              observer.next(false);
              observer.complete();
            },
          });
      });
    } else {
      // Return an observable that emits true and completes immediately
      return of(true);
    }
  }

  refreshToken(token: string): Observable<string> {
    if (token) {
      return new Observable<string>((observer) => {
        return this.http.post<{ token: Token }>(`${this.apiUrl}/refresh-token`, { token: token })
          .pipe(
            map(response => {
              this.setToken(response.token.access_token);

              if (response.token.customerId.length > 0) {
                this.setCustomer(response.token.customerId);
              } else {
                this.removeCustomer();
              }
              observer.next(response.token.access_token);
              observer.complete();
            })
          ).subscribe({
            error: (err) => {
              observer.next("");
              observer.complete();
            },
          });
      });
    } else {
      return of("");
    }
  }

  getSSO(): string {
    var jwt = this.getJwt();
    if (jwt && jwt.role == RoleType.CUSTOMER) {
      return jwt.sso || '';
    } else {
      return '';
    }

  }

  getToken(): string {
    var localKey = localStorage.getItem('cdc_jwt') || '';
    var sessionKey = sessionStorage.getItem('cdc_jwt') || '';
    if (localKey != '' || sessionKey != '') {
      var jwt = this.getJwt(localKey ? localKey : sessionKey)
      if (jwt && jwt.role != RoleType.CUSTOMER) {
        return localKey;
      } else {
        return sessionKey;
      }
    }
    return '';
  }
  setToken(value: string): void {
    this.setSessionStorage('cdc_jwt', value);
  }
  removeToken(): void {
    this.removeSessionStorage('cdc_jwt');
    this.removeSessionStorage('cdc_generalparameters');
  }

  getCustomer(): string {
    return this.getSessionStorage('cdc_customerId') || '';
  }
  setCustomer(value: string): void {
    this.setSessionStorage('cdc_customerId', value);
  }
  removeCustomer(): void {
    this.removeSessionStorage('cdc_customerId');
  }

  getPathName(): string {
    return this.getSessionStorage('cdc_pathName') || '';
  }
  setPathName(value: string): void {
    this.setSessionStorage('cdc_pathName', value);
  }
  removePathName(): void {
    this.removeSessionStorage('cdc_pathName');
  }

  getAuthState(): string {
    return this.getSessionStorage('cdc_authState') || '';
  }
  setAuthState(value: string): void {
    this.setSessionStorage('cdc_authState', value);
  }
  removeAuthState(): void {
    this.removeSessionStorage('cdc_authState');
  }

  getGeneralParameters(): GeneralParameter[] {
    const jsonObj = sessionStorage.getItem('cdc_generalparameters') || '';
    if (jsonObj && jsonObj != 'undefined') {
      return JSON.parse(jsonObj) as GeneralParameter[];
    } else {
      return [];
    }
  }
  setGeneralParameters(value: string): void {
    sessionStorage.setItem('cdc_generalparameters', value);
  }
  removeGeneralParameters(): void {
    sessionStorage.removeItem('cdc_generalparameters');
  }

  getSessionStorage(key: string): string {
    var jwt = this.getJwt();
    if (jwt && jwt.role != RoleType.CUSTOMER) {
      return localStorage.getItem(key) || '';
    } else {
      return sessionStorage.getItem(key) || '';
    }
  }

  setSessionStorage(key: string, value: string): void {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
  }

  removeSessionStorage(key: string): void {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }

  getValidJwt(): Promise<Jwt | null> {
    return new Promise(async (resolve) => {
      const jwt: Jwt | null = this.getJwt();
      if (jwt != null) {
        const currentDate = new Date().getTime();

        if (jwt.role !== null && jwt.role !== undefined &&
          jwt.userId !== null && jwt.userId !== undefined && jwt.userId !== "null" &&
          jwt.email !== null && jwt.email !== undefined && jwt.email !== "null" &&
          jwt.expiration !== null && jwt.expiration !== undefined && !isNaN(jwt.expiration.getTime())
        ) {

          if (jwt.expiration.getTime() > currentDate) {
            return resolve(jwt);
          } else {
            const newToken = await this.refreshToken(jwt.token!).toPromise();
            if (newToken && newToken.length > 0) {
              this.setToken(newToken);
              return resolve(this.getValidJwt());
            } else {
              this.logout("expired");
              resolve(null);
            }
          }
        }
      }
      this.login();
      resolve(null);
    });
  }

  getJwt(token: string = ''): Jwt | null {
    token = token ? token : this.getToken();
    if (token !== null && token !== undefined && token !== "null" && token.length > 0) {
      const tokenDecoded = this.utils.getDecodedAccessToken(token);

      const jwt: Jwt = {
        role: RoleType[tokenDecoded[this.rolesClaim]?.trim().toUpperCase() as keyof typeof RoleType],
        userId: tokenDecoded[this.userIdClaim]?.trim(),
        name: tokenDecoded[this.nameClaim]?.trim(),
        email: tokenDecoded[this.emailClaim]?.trim(),
        expiration: this.utils.parseDateString(tokenDecoded[this.expirationClaim]?.trim()),
        sso: tokenDecoded[this.ssoClaim]?.trim(),
        customerId: tokenDecoded[this.customerIdClaim]?.trim(),
        token: token
      };
      return jwt;
    }
    else {
      return null;
    }
  }

  private userRoleSubject = new BehaviorSubject<RoleType | null>(null);
  async loadUserRole(): Promise<void> {
    const jwt: Jwt | null = await this.getValidJwt();
    const role = jwt?.role ?? null;
    this.userRoleSubject.next(role);
  }

  getUserRoleNotAsync(): BehaviorSubject<RoleType | null> {
    return this.userRoleSubject;
  }

  async getUserRole(): Promise<RoleType | null> {
    const jwt: Jwt | null = await this.getValidJwt();
    if (jwt !== null && jwt.role !== undefined) {
      return jwt.role;
    }
    return null;
  }

  async isAdmin(): Promise<boolean> {
    const role = await this.getUserRole();
    return role === RoleType.ADMIN;
  }

  async isSSO(): Promise<boolean> {
    const role = await this.getUserRole();
    return role === RoleType.CUSTOMER;
  }


  private readonly _onDestroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this._onDestroy$.next(undefined);
    this._onDestroy$.complete();
  }

  handleSSORedirect(path?: string | null): Observable<string> {
    if (path == null) {
      path = '/admin/pages';
    }

    const jwt: Jwt | null = this.getJwt();
    if (jwt == null) {
      return of('/login');
    }

    if (jwt.role === RoleType.ADMIN) {
      return of(path);
    }

    this.removeToken();

    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => this.transformToken(true)),
      switchMap((response: boolean) => {
        if (response) {
          const currentJwt = this.getJwt();
          return of(currentJwt?.role === RoleType.ADMIN ? path : '/');
        }
        return of('/');
      })
    );
  }
}
